import { computed, ref } from "vue"
import { initScriptTag } from "./browser"

/**
 * password validation regex pattern
 * used with input type="password" in the pattern attribute for html5 validation
 * exported as a raw string to support the escape backslash characters
 */
export const PasswordPattern = String.raw`(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*\(\)_+=\-]).{8,}`

/**
 * formats an array of strings into input fields options for radios, selects, checkboxes
 * @param options array of strings to format into input options
 */
export const arrayToOptions = (options: string[]) => {
  return options.map((o) => {
    return {
      label: o,
      value: o,
    }
  })
}

/**
 * maxDateToday is a valid date string for "Today" to help restict input type date fields
 * from allowing future date entries when they should be restricted to past dates.
 */
export const maxDateToday = new Date().toISOString().split("T")[0]

/**
 * minDateTomorrow returns a date string for "Tomorrow" to help restrict input type date fields
 * from allowing past date entries - including today - when they should be restricted to past dates.
 */
export const minDateTomorrow = () => {
  const d = new Date()
  d.setDate(new Date().getDate() + 1)

  return d.toISOString().split("T")[0]
}

export interface CaptchaRequest {
  captchaAction: string
  captchaResponse: string
}

/**
 * initCaptchaAssets loads the Google Captcha JS library
 * ensuring it is only loaded once.
 * https://developers.google.com/recaptcha/docs/v3#tips
 * https://web.dev/trusted-types/
 */
const initCaptchaAssets: () => Promise<true> = (() => {
  let once: Promise<true> | undefined

  const init = async (): Promise<true> => {
    await initScriptTag(
      `https://www.google.com/recaptcha/api.js?render=${
        import.meta.env.VITE_APP_GOOGLE_CAPTCHA_KEY
      }&trustedtypes=true`
    )
    return await Promise.resolve(true)
  }

  return async () => {
    if (!once) {
      once = init()
    }

    return once
  }
})()

// https://developers.google.com/recaptcha/docs/v3
// reCAPTCHA tokens expire after two minutes, and can only be verified once to prevent replay attacks.
// make sure to call execute when the user takes the action rather than on page load.
export const useCaptcha = (action: string) => {
  let captcha: ReCaptchaV2.ReCaptcha | undefined = undefined
  const captchaReady = ref<boolean>(false)
  const captchaRespToken = ref<string>("")

  initCaptchaAssets().then(() => {
    window.grecaptcha.ready(() => {
      captcha = window.grecaptcha
      captchaReady.value = true
    })
  })

  const captchaRequest = computed((): CaptchaRequest => {
    return {
      captchaAction: action,
      captchaResponse: captchaRespToken.value,
    }
  })

  const captchaExecute = async () => {
    if (captchaReady.value === false) {
      throw new Error("Captcha service unavailable")
    }

    const token = await captcha.execute(
      import.meta.env.VITE_APP_GOOGLE_CAPTCHA_KEY,
      { action: action }
    )

    captchaRespToken.value = token
    return captchaRequest.value
  }

  return {
    captchaReady,
    captchaRequest,
    captchaExecute,
  }
}

/**
 * recordToMultiselectOptions formats a record of arrays into an
 * an options set suitable for MultiSelect components
 * @param records Record<string, Array<string>>
 * @return Array<{ label: string; options: Array<{ label: string; value: string }> }>
 */
export const recordToMultiselectOptions = (
  records: Record<string, Array<string>>
): Array<{
  label: string
  options: Array<{
    label: string
    value: string
  }>
}> => {
  const options: {
    label: string
    options: { label: string; value: string }[]
  }[] = []

  for (const [key, record] of Object.entries(records)) {
    options.push({
      label: key,
      options: record.map((v) => {
        return {
          label: v,
          value: v,
        }
      }),
    })
  }

  return options
}
